/**
 * Deep-merge helpers for locale resources, shared by the runtime loader
 * (loadLocale.js), the Jest stub (jest/loadLocaleStub.js) and the Node
 * scripts (scripts/i18n-check.mjs, scripts/i18n-merge-fragments.mjs).
 * Plain ESM with no imports so all of them can load it.
 */

export function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

/**
 * Merge `source` into `target` (mutates and returns `target`). Nested objects
 * merge recursively; any other value in `source` replaces the one in
 * `target`. Existing key order is kept and new keys are appended, so folding
 * fragments into a base file only adds lines.
 *
 * `onConflict(path, oldValue, newValue)` is called when a defined value is
 * replaced by a different one (including object <-> string clashes).
 */
export function deepMerge(target, source, onConflict, path = []) {
  if (!isPlainObject(source)) return target;
  for (const [key, value] of Object.entries(source)) {
    const here = [...path, key];
    const current = target[key];
    if (isPlainObject(value) && isPlainObject(current)) {
      deepMerge(current, value, onConflict, here);
    } else if (isPlainObject(value)) {
      if (current !== undefined && onConflict) onConflict(here.join('.'), current, value);
      target[key] = deepMerge({}, value, onConflict, here);
    } else {
      if (current !== undefined && current !== value && onConflict) {
        onConflict(here.join('.'), current, value);
      }
      target[key] = value;
    }
  }
  return target;
}

/** Merge a list of resource objects (base first, then fragments) into a new object. */
export function mergeLocaleParts(parts, onConflict) {
  const out = {};
  for (const part of parts) deepMerge(out, part, onConflict);
  return out;
}

/**
 * Flatten nested resources into { 'a.b.c': 'value' } (leaf values only).
 * Arrays (used with t(key, { returnObjects: true })) are walked by index:
 * 'landing.features.0.title'.
 */
export function flattenKeys(obj, prefix = '', out = {}) {
  if (!isPlainObject(obj) && !Array.isArray(obj)) return out;
  for (const [key, value] of Object.entries(obj)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isPlainObject(value) || Array.isArray(value)) flattenKeys(value, path, out);
    else out[path] = value;
  }
  return out;
}
